// Componentes base do site — mesmo vocabulário visual do bi.web (cartão, selo, estados).
//
// Regra que atravessa as listas: SEMPRE os três estados — carregando, vazio e erro. Uma lista que
// fica em branco enquanto carrega parece vazia; uma que fica em branco no erro parece "não tem
// nada" e esconde o problema.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { mensagemDeErro } from '../api/client';
import { useTema } from '../lib/tema';
import { ha } from '../lib/formato';
import type { CorSelo } from '../lib/rotulos';
import { usePilhaModal } from './desktop/pilhaModais';
import { IconMoon, IconSun, IconX } from './desktop/icones';
import iconePlugChef from '../assets/plugchef-app-icon.svg';

// ─── Estados ────────────────────────────────────────────────────────────────────────────────────

export function Carregando({ linhas = 3, altura = 'h-16' }: { linhas?: number; altura?: string }): JSX.Element {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Carregando">
      {Array.from({ length: linhas }, (_, i) => (
        <div key={i} className={`card ${altura} animate-pulseSuave`} />
      ))}
    </div>
  );
}

export function TelaCarregando({ texto = 'Carregando…' }: { texto?: string }): JSX.Element {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <div className="text-sm text-mut animate-pulseSuave">{texto}</div>
    </div>
  );
}

export function Vazio({ titulo, detalhe, acao, icone }: { titulo: string; detalhe?: string; acao?: ReactNode; icone?: ReactNode }): JSX.Element {
  return (
    <div className="card p-8 text-center">
      {icone ? <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-card2 text-mut">{icone}</div> : null}
      <div className="text-sm text-mut font-medium">{titulo}</div>
      {detalhe ? <div className="text-xs text-mut2 mt-1 max-w-md mx-auto">{detalhe}</div> : null}
      {acao ? <div className="mt-4">{acao}</div> : null}
    </div>
  );
}

export function ErroConsulta({ erro, aoTentar }: { erro: unknown; aoTentar?: () => void }): JSX.Element {
  return (
    <div className="card p-6 text-center border-bad/30">
      <div className="text-sm text-bad font-medium">{mensagemDeErro(erro, 'Não foi possível carregar.')}</div>
      {aoTentar ? (
        <button className="btn-secundario mt-3" onClick={aoTentar}>
          Tentar de novo
        </button>
      ) : null}
    </div>
  );
}

/** Mensagem de erro de formulário (o `error` da API, como veio). */
export function AvisoErro({ children }: { children: ReactNode }): JSX.Element | null {
  if (!children) return null;
  return (
    <div role="alert" className="text-xs text-bad bg-bad/10 border border-bad/25 rounded-lg px-3 py-2">
      {children}
    </div>
  );
}

export function AvisoOk({ children }: { children: ReactNode }): JSX.Element | null {
  if (!children) return null;
  return (
    <div role="status" className="text-xs text-good bg-good/10 border border-good/25 rounded-lg px-3 py-2">
      {children}
    </div>
  );
}

// ─── Estrutura ──────────────────────────────────────────────────────────────────────────────────

export function Secao({
  titulo, descricao, acao, children,
}: {
  titulo: string;
  descricao?: ReactNode;
  acao?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="mt-8 first:mt-0">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h2 className="text-base font-bold">{titulo}</h2>
          {descricao ? <p className="text-xs text-mut mt-0.5">{descricao}</p> : null}
        </div>
        {acao ? <div className="shrink-0 flex flex-wrap gap-2">{acao}</div> : null}
      </div>
      {children}
    </section>
  );
}

export function TituloPagina({ titulo, detalhe, acao }: { titulo: ReactNode; detalhe?: ReactNode; acao?: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
      <div className="min-w-0">
        <h1 className="text-xl sm:text-2xl font-extrabold tracking-tight break-words">{titulo}</h1>
        {detalhe ? <div className="text-sm text-mut mt-1">{detalhe}</div> : null}
      </div>
      {acao ? <div className="flex flex-wrap gap-2 shrink-0">{acao}</div> : null}
    </div>
  );
}

/** Par rótulo/valor das telas de detalhe. */
export function Info({ rotulo, children }: { rotulo: string; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <div className="rotulo">{rotulo}</div>
      <div className="text-sm mt-0.5 break-words">{children}</div>
    </div>
  );
}

// ─── Selos ──────────────────────────────────────────────────────────────────────────────────────

/** O teste grátis do contratante: "Em teste até dd/mm" ou "Teste vencido em dd/mm" (o dia de Brasília). */
export function SeloTeste({ ate }: { ate: string | null | undefined }): JSX.Element | null {
  if (!ate) return null;
  const hoje = new Date().toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
  const dia = ate.slice(0, 10).split('-').reverse().slice(0, 2).join('/');
  return ate.slice(0, 10) >= hoje ? <Selo texto={`Em teste até ${dia}`} cor="atencao" /> : <Selo texto={`Teste vencido em ${dia}`} cor="ruim" />;
}

export function Selo({ texto, cor = 'neutro', titulo }: { texto: string; cor?: CorSelo; titulo?: string }): JSX.Element {
  const cores = {
    neutro: 'bg-card2 text-mut border-bd',
    bom: 'bg-good/10 text-good border-good/25',
    atencao: 'bg-amber/10 text-amber border-amber/25',
    ruim: 'bg-bad/10 text-bad border-bad/25',
    accent: 'bg-accent/10 text-accent border-accent/25',
  } as const;
  return (
    <span
      title={titulo}
      className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold border whitespace-nowrap ${cores[cor]}`}
    >
      {texto}
    </span>
  );
}

/** Bolinha verde/cinza + "Online" / "Offline · há X". */
export function Conexao({
  online, ultimo, agora, compacto,
}: {
  online: boolean;
  ultimo: string | null;
  agora: number;
  compacto?: boolean;
}): JSX.Element {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs min-w-0">
      <span
        className={`w-2 h-2 rounded-full shrink-0 ${online ? 'bg-good shadow-[0_0_0_3px_rgba(34,197,94,0.18)]' : 'bg-mut2'}`}
        aria-hidden
      />
      <span className={online ? 'text-good font-semibold' : 'text-mut'}>{online ? 'Online' : 'Offline'}</span>
      {!compacto || !online ? (
        <span className="text-mut2 truncate">· {ultimo ? `última conversa ${ha(ultimo, agora)}` : 'nunca conversou'}</span>
      ) : null}
    </span>
  );
}

// ─── Formulário ─────────────────────────────────────────────────────────────────────────────────

export function Campo({
  rotulo, dica, children,
}: {
  rotulo: string;
  dica?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <label className="block">
      <span className="rotulo">{rotulo}</span>
      <div className="mt-1">{children}</div>
      {dica ? <span className="text-[11px] text-mut2 mt-1 block">{dica}</span> : null}
    </label>
  );
}

// ─── Modal ──────────────────────────────────────────────────────────────────────────────────────

/**
 * Janela sobre a página. Fecha com Esc e clique fora — exceto enquanto `travado` (requisição em
 * andamento: fechar no meio deixaria a pessoa sem saber se a ação valeu).
 */
export function Modal({
  titulo, aoFechar, children, rodape, travado, largura = 'max-w-lg',
}: {
  titulo: ReactNode;
  aoFechar: () => void;
  children: ReactNode;
  rodape?: ReactNode;
  travado?: boolean;
  largura?: string;
}): JSX.Element {
  // Refs: quem usa o Modal passa `aoFechar` como função nova a cada render; com ela nas dependências
  // o efeito (ouvinte de tecla e trava da rolagem) seria desfeito e refeito o tempo todo.
  const travadoRef = useRef(travado);
  travadoRef.current = travado;
  const aoFecharRef = useRef(aoFechar);
  aoFecharRef.current = aoFechar;
  // Sobre outro modal (a confirmação de excluir por cima do produto), o Esc fecha só este.
  const pilha = usePilhaModal();

  useEffect(() => {
    function tecla(e: KeyboardEvent): void {
      if (e.key === 'Escape' && !travadoRef.current && pilha.ehTopo()) {
        e.stopPropagation();
        aoFecharRef.current();
      }
    }
    window.addEventListener('keydown', tecla);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', tecla);
      document.body.style.overflow = overflow;
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !travado) aoFechar();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={`card w-full ${largura} max-h-[92vh] flex flex-col rounded-b-none sm:rounded-b-card animate-slideup`}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-bd">
          <h2 className="text-base font-bold min-w-0">{titulo}</h2>
          <button
            type="button"
            className="anel-foco -mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-mut hover:bg-card2 hover:text-tx disabled:opacity-40"
            onClick={aoFechar}
            disabled={travado}
            aria-label="Fechar"
          >
            <IconX size={15} />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
        {rodape ? <div className="px-5 py-3 border-t border-bd flex flex-wrap justify-end gap-2">{rodape}</div> : null}
      </div>
    </div>
  );
}

// ─── Utilidades de tela ─────────────────────────────────────────────────────────────────────────

export function BotaoTema({ className = '' }: { className?: string }): JSX.Element {
  const [tema, alternar] = useTema();
  return (
    <button
      type="button"
      onClick={alternar}
      className={`w-8 h-8 inline-flex items-center justify-center rounded-lg text-mut hover:text-tx hover:bg-card2 transition-colors shrink-0 ${className}`}
      title={tema === 'dark' ? 'Usar tema claro' : 'Usar tema escuro'}
      aria-label={tema === 'dark' ? 'Usar tema claro' : 'Usar tema escuro'}
    >
      {tema === 'dark' ? <IconSun size={16} /> : <IconMoon size={16} />}
    </button>
  );
}

/**
 * Copia para a área de transferência. `navigator.clipboard` só existe em contexto seguro (HTTPS ou
 * localhost); fora dele cai no `execCommand` — para a senha provisória não ficar sem jeito de copiar.
 */
export async function copiarTexto(texto: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(texto);
      return true;
    }
  } catch {
    /* tenta o caminho antigo abaixo */
  }
  try {
    const area = document.createElement('textarea');
    area.value = texto;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch {
    return false;
  }
}

export function BotaoCopiar({ texto, rotulo = 'Copiar' }: { texto: string; rotulo?: string }): JSX.Element {
  const [estado, setEstado] = useState<'parado' | 'ok' | 'falhou'>('parado');
  useEffect(() => {
    if (estado === 'parado') return;
    const t = window.setTimeout(() => setEstado('parado'), 2500);
    return () => window.clearTimeout(t);
  }, [estado]);
  return (
    <button
      type="button"
      className="btn-secundario"
      onClick={() => void copiarTexto(texto).then((ok) => setEstado(ok ? 'ok' : 'falhou'))}
    >
      {estado === 'ok' ? 'Copiado ✓' : estado === 'falhou' ? 'Copie à mão' : rotulo}
    </button>
  );
}

/** Paginação simples (anterior/próxima) com "1–50 de 312". */
export function Paginacao({
  pagina, porPagina, total, aoMudar,
}: {
  pagina: number;
  porPagina: number;
  total: number;
  aoMudar: (p: number) => void;
}): JSX.Element | null {
  if (total <= porPagina && pagina === 1) return null;
  const ultima = Math.max(1, Math.ceil(total / porPagina));
  const de = total === 0 ? 0 : (pagina - 1) * porPagina + 1;
  const ate = Math.min(total, pagina * porPagina);
  return (
    <div className="flex items-center justify-between gap-3 mt-3 text-xs text-mut">
      <span className="num">
        {de}–{ate} de {total}
      </span>
      <div className="flex gap-2">
        <button className="btn-secundario py-1.5" disabled={pagina <= 1} onClick={() => aoMudar(pagina - 1)}>
          ← Anterior
        </button>
        <button className="btn-secundario py-1.5" disabled={pagina >= ultima} onClick={() => aoMudar(pagina + 1)}>
          Próxima →
        </button>
      </div>
    </div>
  );
}

/** A marca do produto (a mesma do Desktop, components/MarcaPlugChef.tsx): o ícone e o nome em
 *  Montserrat — "Plug" normal e "Chef" em negrito, na cor do texto — e a área em destaque ("Admin").
 *  As medidas são em `em`: o tamanho vem da classe de fonte (`className`), então muda com a largura da
 *  tela (`text-sm sm:text-base`). `area={null}` mostra só o nome (a tela de entrada põe a área num selo). */
export function Marca({ area = 'Admin', className = 'text-sm sm:text-base' }: { area?: string | null; className?: string }): JSX.Element {
  return (
    <span className={`inline-flex items-center gap-[0.45em] whitespace-nowrap leading-none ${className}`}>
      <img src={iconePlugChef} alt="" className="h-[1.55em] w-[1.55em] shrink-0" />
      <span className="text-tx" style={{ fontFamily: "'Montserrat', sans-serif", letterSpacing: '-0.01em' }}>
        <span className="font-normal">Plug</span>
        <span className="font-bold">Chef</span>
        {area ? <span className="font-semibold text-accent"> {area}</span> : null}
      </span>
    </span>
  );
}
